import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerApi } from "@/lib/api";
import SEO from "@/components/SEO";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function CustomerLogin() {
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/konto";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsVerify(false);
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success("Willkommen zurück!");
      navigate(from, { replace: true });
    } else {
      setError(res.error);
      if (res.status === 403) setNeedsVerify(true);
    }
  };

  const resend = async () => {
    await customerApi.post("/customer/resend-verification", { email: form.email }).catch(() => {});
    toast.success("Bestätigungs-E-Mail erneut gesendet (sofern das Konto existiert).");
  };

  return (
    <div className="on-dark violet-canvas flex-1 w-full flex items-center justify-center px-5 py-20">
      <SEO title="Anmelden" description="Melde dich bei GYSUMO an." />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo variant="butterfly" className="h-14 w-auto mx-auto mb-4" />
          <h1 className="font-serif text-4xl font-light text-[#FFF9F2]">Anmelden</h1>
          <p className="text-[#D8B9EA] mt-2 text-sm">Für Anfragen und Bestellanfragen zu Werken.</p>
        </div>
        <form onSubmit={submit} className="space-y-5 bg-[#FFF9F2] rounded-md p-7" data-testid="customer-login-form">
          <div>
            <Label htmlFor="cl-email">E-Mail</Label>
            <Input id="cl-email" data-testid="login-email" type="email" required value={form.email} onChange={upd("email")} className="mt-1.5 bg-white" />
          </div>
          <div>
            <Label htmlFor="cl-pw">Passwort</Label>
            <Input id="cl-pw" data-testid="login-password" type="password" required value={form.password} onChange={upd("password")} className="mt-1.5 bg-white" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          {needsVerify && (
            <button type="button" onClick={resend} data-testid="resend-verification" className="text-sm text-[#632A8E] underline">
              Bestätigungs-E-Mail erneut senden
            </button>
          )}
          <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md h-12">
            {loading ? "Anmelden…" : "Anmelden"}
          </Button>
          <p className="text-sm text-center">
            <Link to="/passwort-vergessen" data-testid="link-forgot" className="text-[#632A8E] underline">Passwort vergessen?</Link>
          </p>
          <p className="text-sm text-center text-[#6E5A82]">
            Noch kein Konto? <Link to="/registrieren" data-testid="link-register" className="text-[#632A8E] underline">Jetzt registrieren</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
